import Path from "@core/FileProvider/Path/Path";
import generateUniqueID from "@core/utils/generateUniqueID";
import assert from "assert";
import { agentConfig } from "./agentConfig";
import type { AgentFileStore } from "./agentFileStore";

const ATTACHMENT_DIR_ID_LENGTH = 12;

export type StoredAttachment = {
	originalFilename: string;
	storagePath: string;
	size: number;
	mime: string;
};

export type AgentAttachment = StoredAttachment & { handle: string };

export type ChatAttachment = AgentAttachment | StoredAttachment;

export type AgentAttachmentInput = {
	name: string;
	mime: string;
	size: number;
	content: string | Uint8Array;
	contentEncoding?: "utf-8" | "base64";
};

export class AgentAttachmentStore {
	constructor(private readonly _fileStore: AgentFileStore) {}

	async put(sessionId: string, attachments: AgentAttachmentInput[]): Promise<StoredAttachment[]> {
		const result: StoredAttachment[] = [];
		const writtenDirs: string[] = [];

		try {
			for (const attachment of attachments) {
				const filename = AgentAttachmentStore._toFilename(attachment.name);
				AgentAttachmentStore.assertAttachmentExtension(filename);
				const bytes = AgentAttachmentStore._toBytes(attachment);

				const idDir = `${this._dir(sessionId)}/${generateUniqueID(ATTACHMENT_DIR_ID_LENGTH)}`;
				writtenDirs.push(idDir);
				await this._fileStore.writeFile(
					`${idDir}/${filename}`,
					Buffer.from(bytes.buffer, bytes.byteOffset, bytes.byteLength),
				);

				result.push({
					originalFilename: filename,
					storagePath: `${idDir}/${filename}`,
					size: bytes.byteLength,
					mime: attachment.mime,
				});
			}

			return result;
		} catch (err) {
			await writtenDirs.forEachAsync((dir) => this._fileStore.deletePath(dir));
			throw err;
		}
	}

	async read(sessionId: string, storagePath: string): Promise<{ filename: string; bytes: Uint8Array } | null> {
		const path = new Path(storagePath);
		const isRaw = this._isUnder(this._dir(sessionId), path);
		assert(isRaw || this._isUnder(this._legacyDir(sessionId), path), "Attachment not found");

		if (isRaw) {
			const raw = await this._fileStore.readBinary(path.value);
			if (!raw) return null;
			return {
				filename: path.nameWithExtension,
				bytes: new Uint8Array(raw.buffer, raw.byteOffset, raw.byteLength),
			};
		}

		const legacy = await this._fileStore.readFile(path.value);
		if (legacy == null) return null;
		const decoded = Buffer.from(legacy, "base64");
		return {
			filename: path.nameWithExtension,
			bytes: new Uint8Array(decoded.buffer, decoded.byteOffset, decoded.byteLength),
		};
	}

	async resolveUploaded(sessionId: string, attachment: StoredAttachment): Promise<StoredAttachment> {
		const path = new Path(attachment.storagePath);
		const uploaded = this._isUnder(this._dir(sessionId), path) && (await this._fileStore.exists(path.value));
		assert(uploaded, `Attachment not found: ${attachment.originalFilename}`);
		return attachment;
	}

	async delete(sessionId: string, storagePath: string): Promise<void> {
		const path = new Path(storagePath);
		assert(this._isUnder(this._dir(sessionId), path), "Attachment not found");
		await this._fileStore.deletePath(path.parentDirectoryPath.value);
	}

	static assertAttachmentExtension(filename: string): void {
		const ext = new Path(filename).extension?.toLowerCase() ?? "";
		assert(
			ext && agentConfig.allowedAttachmentExtensions.includes(ext),
			`Unsupported attachment extension: ${ext || "(none)"}`,
		);
	}

	private _isUnder(dir: string, path: Path): boolean {
		return path.value.startsWith(`${dir}/`) && !path.value.split("/").includes("..");
	}

	private _dir(sessionId: string): string {
		return `sessions/${sessionId}/files`;
	}

	private _legacyDir(sessionId: string): string {
		return `sessions/${sessionId}/attachments`;
	}

	private static _toFilename(name: string): string {
		return new Path(name).nameWithExtension.replace(/[/\\]/g, "_");
	}

	private static _toBytes(attachment: AgentAttachmentInput): Uint8Array {
		if (typeof attachment.content !== "string") return attachment.content;
		const encoding = attachment.contentEncoding === "utf-8" ? "utf8" : "base64";
		const decoded = Buffer.from(attachment.content, encoding);
		return new Uint8Array(decoded.buffer, decoded.byteOffset, decoded.byteLength);
	}
}
